






































































import Vue from "vue";
import { RawLocation } from "vue-router";
import BidModel, { ItemState } from "@/models/BidModel";
import OptionModel from "@/models/OptionModel";
import session from "@/global/session";
import watchlist from "@/global/watchlist";
import AuctionItemCard from "@/components/AuctionItemCard.vue";

const POLL_INTERVAL_MS = 4000;

export default Vue.extend({
	name: "AuctionBidding",

	components: { AuctionItemCard },

	data() {
		return {
			items: [] as ItemState[],
			auctionStatus: "open" as "open" | "closed",
			eventName: "" as string,
			eventLogo: "" as string,
			isLoading: true as boolean,
			isRefreshing: false as boolean,
			search: "" as string,
			categoryFilter: "" as string,
			statusFilter: "all" as string,
			watchlistItems: [] as number[],
			orderedItemNumbers: [] as number[],
			focusItem: null as number | null,
			pollTimer: null as number | null,
		};
	},

	computed: {
		bidder() {
			return session.bidder();
		},

		isAuthenticated(): boolean {
			return session.isAuthenticated();
		},

		// Send the visitor to OTP login and bring them back to the auction
		// once they've signed in.
		loginLink(): RawLocation {
			return { name: "BidderLogin", query: { redirect: "/auction", context: "auction" } };
		},

		bidderFullName(): string {
			const b = this.bidder;
			if (!b) return "";
			return [b.firstName, b.lastName].filter(Boolean).join(" ");
		},

		checkoutLink(): string {
			const bidNumber = session.bidNumber();
			return bidNumber != null ? `/checkout/${bidNumber}` : "";
		},

		categories(): string[] {
			const set: { [key: string]: boolean } = {};
			this.items.forEach((item) => {
				if (item.category) set[item.category] = true;
			});
			return Object.keys(set).sort();
		},

		hasActiveFilters(): boolean {
			return this.search !== "" || this.categoryFilter !== "" || this.statusFilter !== "all";
		},

		filteredItems(): ItemState[] {
			const term = this.search.trim().toLowerCase();

			const matches = this.items.filter((item) => {
				// Deep-link focus (from an outbid text): show only this item.
				if (this.focusItem !== null && item.itemNumber !== this.focusItem) return false;

				// Text search: name or item number.
				if (term) {
					const name = (item.name || "").toLowerCase();
					const num = String(item.itemNumber);
					if (name.indexOf(term) === -1 && num.indexOf(term) === -1) return false;
				}

				// Category.
				if (this.categoryFilter && item.category !== this.categoryFilter) return false;

				// Status.
				if (this.statusFilter === "watchlist" && this.watchlistItems.indexOf(item.itemNumber) === -1) return false;
				if (this.statusFilter === "leading" && !item.youAreLeading) return false;
				if (this.statusFilter === "outbid" && !(item.youHaveBid && !item.youAreLeading)) return false;
				if (this.statusFilter === "open" && item.bidCount > 0) return false;

				return true;
			});

			// Order follows the snapshot taken at page load (see applyOrder), so
			// placing a bid or a background poll never reorders items under the
			// user. Items not in the snapshot fall to the end.
			const orderIndex = (n: number): number => {
				const i = this.orderedItemNumbers.indexOf(n);
				return i === -1 ? Number.MAX_SAFE_INTEGER : i;
			};

			return matches.slice().sort((a, b) => orderIndex(a.itemNumber) - orderIndex(b.itemNumber));
		},
	},

	created() {
		// Mobile bidding is disabled via the Options tab — send visitors home.
		if (!this.$store.getters.isMobileBiddingEnabled) this.$router.replace({ name: "Home" });
	},

	async mounted() {
		if (!this.$store.getters.isMobileBiddingEnabled) return;

		const itemParam = this.$route.query.item;
		if (itemParam) {
			const n = parseInt(itemParam as string, 10);
			if (!isNaN(n)) this.focusItem = n;
		}

		this.watchlistItems = watchlist.list(session.bidNumber());

		try {
			const [nameOpt, logoOpt] = await Promise.all([OptionModel.getByKey("event_name"), OptionModel.getByKey("event_logo")]);
			if (nameOpt && nameOpt.value) this.eventName = nameOpt.value;
			if (logoOpt && logoOpt.value) this.eventLogo = logoOpt.value;
		} catch (e) {
			// Non-fatal — the sub-header/logo just won't show.
		}

		await this.refresh();
		this.applyOrder();
		this.isLoading = false;
		this.pollTimer = window.setInterval(this.refresh, POLL_INTERVAL_MS);
	},

	beforeDestroy() {
		if (this.pollTimer !== null) window.clearInterval(this.pollTimer);
	},

	methods: {
		async refresh() {
			this.isRefreshing = true;
			try {
				const state = await BidModel.state();

				// We sent a token but the server didn't accept it — the session
				// expired or was revoked. Catch it here (on the poll) rather than
				// letting the guest keep browsing as a silent anonymous user and
				// only discover it when a bid fails.
				if (session.token() && state.authenticated === false) {
					this.handleExpiredSession();
					return;
				}

				this.items = state.items;
				this.auctionStatus = state.auctionStatus;
			} catch (e) {
				// Keep last known state on a failed poll; don't spam the user.
			}
			this.isRefreshing = false;
		},

		// Sign the guest out cleanly and send them back to log in, returning
		// them to where they were once they do.
		handleExpiredSession() {
			if (this.pollTimer !== null) {
				window.clearInterval(this.pollTimer);
				this.pollTimer = null;
			}
			session.clear();
			this.$router.push({
				name: "BidderLogin",
				query: { redirect: this.$route.fullPath, context: "auction", expired: "1" },
			} as RawLocation);
		},

		// Snapshot the display order once (on full page load): losing first, then
		// watchlisted, then the rest. Held stable for the session so live updates
		// don't shuffle items under the user.
		applyOrder() {
			const rank = (item: ItemState): number => {
				if (item.youHaveBid && !item.youAreLeading) return 0;
				if (this.watchlistItems.indexOf(item.itemNumber) !== -1) return 1;
				return 2;
			};

			this.orderedItemNumbers = this.items
				.slice()
				.sort((a, b) => rank(a) - rank(b))
				.map((item) => item.itemNumber);
		},

		refreshNow() {
			this.refresh();
		},

		clearFilters() {
			this.search = "";
			this.categoryFilter = "";
			this.statusFilter = "all";
		},

		isFavorite(itemNumber: number): boolean {
			return this.watchlistItems.indexOf(itemNumber) !== -1;
		},

		toggleFavorite(itemNumber: number) {
			this.watchlistItems = watchlist.toggle(session.bidNumber(), itemNumber).slice();
		},

		// A placed bid auto-adds the item to the bidder's watchlist.
		addBidToWatchlist(itemNumber: number) {
			this.watchlistItems = watchlist.add(session.bidNumber(), itemNumber).slice();
		},

		clearFocus() {
			this.focusItem = null;
			if (this.$route.query.item) this.$router.replace({ name: "Auction" });
		},

		logout() {
			session.clear();
			this.$router.push({ name: "BidderLogin" } as RawLocation);
		},
	},
});
